/**
 * Photo Utilities - Centralized photo selection and URL generation
 *
 * This module provides utilities for consistently selecting and displaying
 * identity photos across the application. It implements a priority-based
 * fallback chain to ensure the most appropriate photo is always used.
 */

import { getAbsoluteImageUrl } from './imageUrl';

export interface PersonPhoto {
  id?: string;
  photo_url?: string;
  is_primary?: boolean;
  photo_type?: string;
  is_active?: boolean;
}

export interface Person {
  id: string;
  identity_photo_url?: string;
  profile_picture_url?: string;
  identity_photos?: PersonPhoto[];
}

/**
 * Get primary photo URL with fallback chain
 *
 * Priority order:
 * 1. identity_photo_url (backend-provided primary photo)
 * 2. Primary from identity_photos array (is_primary = true)
 * 3. First facial_recognition photo from array
 * 4. First active photo from array
 * 5. profile_picture_url (legacy field)
 * 6. null (no photo available)
 *
 * @param person - Person object with photo data
 * @returns Absolute URL to the primary photo, or null if unavailable
 */
export function getPrimaryPhotoUrl(person: Person | null | undefined): string | null {
  if (!person) return null;

  // 1. Backend-provided primary photo (most reliable)
  if (person.identity_photo_url) {
    return getAbsoluteImageUrl(person.identity_photo_url);
  }

  // 2-4. Search identity_photos array with fallback chain
  if (person.identity_photos?.length) {
    const photos = person.identity_photos;

    // 2. Look for photo explicitly marked as primary
    const primary = photos.find(p => p.is_primary && p.is_active !== false);
    if (primary?.photo_url) {
      return getAbsoluteImageUrl(primary.photo_url);
    }

    // 3. Look for facial_recognition photo (not a document)
    const facial = photos.find(p =>
      p.photo_type === 'facial_recognition' && p.is_active !== false
    );
    if (facial?.photo_url) {
      return getAbsoluteImageUrl(facial.photo_url);
    }

    // 4. Take any active photo
    const first = photos.find(p => p.is_active !== false);
    if (first?.photo_url) {
      return getAbsoluteImageUrl(first.photo_url);
    }
  }

  // 5. Legacy profile_picture_url field
  if (person.profile_picture_url) {
    return getAbsoluteImageUrl(person.profile_picture_url);
  }

  // 6. No photo available
  return null;
}

/**
 * Get photo URL from an access event
 *
 * Priority order:
 * 1. identity_photo_url
 * 2. face_photo_url
 * 3. person_photo_url
 * 4. profile_picture_url
 * 5. null
 *
 * @param event - Access event object
 * @returns Absolute URL to the event photo, or null if unavailable
 */
export function getEventPhotoUrl(event: any): string | null {
  if (!event) return null;

  // Try different photo URL fields in priority order
  const photoUrl =
    event.identity_photo_url ||
    event.face_photo_url ||
    event.person_photo_url ||
    event.profile_picture_url ||
    null;

  return photoUrl ? getAbsoluteImageUrl(photoUrl) : null;
}

/**
 * Get avatar initials from person name
 * Used as fallback when no photo is available
 *
 * @param firstName - Person's first name
 * @param lastName - Person's last name
 * @returns Two-letter initials (e.g., "JD" for "John Doe")
 */
export function getAvatarInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim()?.[0] || '';
  const last = lastName?.trim()?.[0] || '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Check if a photo URL is valid (not empty/null/undefined)
 *
 * @param photoUrl - Photo URL to check
 * @returns true if URL is valid, false otherwise
 */
export function isValidPhotoUrl(photoUrl: string | null | undefined): boolean {
  return Boolean(photoUrl && photoUrl.trim().length > 0);
}
